import Footer from '@/components/Footer';
import Header from '@/components/Header';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { fadeIn, slideUp, staggerChildren } from '@/utils/animations';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronDown, ChevronUp, FileText, MapPin, Shield, TreePine, Truck } from "lucide-react";
import { useEffect, useState } from 'react';
import { FaFileAlt, FaMapMarkerAlt, FaShieldAlt, FaTree, FaTruck } from 'react-icons/fa';
import { Link, useLocation, useNavigate } from 'react-router-dom';

export default function ServicesPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const [selectedService, setSelectedService] = useState(null);

    // Define services with detailed process steps
    const services = [
        {
            id: "tree-cutting",
            icon: <TreePine className="h-12 w-12 text-green-600" />,
            reactIcon: FaTree,
            title: "Tree Cutting Permits",
            description: "Apply for permits to legally cut trees, ensuring sustainable forest management practices.",
            process: [
                "Determine if the trees are on public or private land and verify eligibility with PENRO Marinduque.",
                "Submit an online application form or letter of intent via this website.",
                "Upload required documents: proof of land ownership (title/tax declaration), tree inventory (species, diameter, location), and an environmental management plan.",
                "Pay the application fee (approx. PHP 1,000+ for public land, varies for private land, subject to change).",
                "Schedule a site inspection with PENRO Marinduque staff.",
                "Receive the permit decision within 20–40 working days (public land) or 15–30 days (private land) after inspection.",
                "If approved, download and print your tree cutting permit digitally."
            ]
        },
        {
            id: "chainsaw-registration",
            icon: <Shield className="h-12 w-12 text-green-600" />,
            reactIcon: FaShieldAlt,
            title: "Chainsaw Registration",
            description: "Register your chainsaw to comply with regulations and promote responsible use of forestry equipment.",
            process: [
                "Fill out the online chainsaw registration form on the PermitTree website or obtain a form from the office.",
                "Submit proof of ownership (receipt or deed of sale) for the chainsaw.",
                "Provide chainsaw specifications and photos (serial number, model, etc.).",
                "Pay the registration fee.",
                "Schedule a chainsaw inspection at the nearest PermitTree office.",
                "Receive a digital registration certificate within 7 working days after approval."
            ]
        },
        {
            id: "certificate-verification",
            icon: <FileText className="h-12 w-12 text-green-600" />,
            reactIcon: FaFileAlt,
            title: "Certificate of Verification (COV)",
            description: "Obtain a COV for transporting planted trees and non-timber forest products within private land.",
            process: [
                "Determine eligibility (planted trees, non-premium timber, non-timber products, or private land wood; bamboo exempt per DAO 2021-26).",
                "Submit an online application for COV via this website.",
                "Upload proof of land ownership (title/tax declaration) and plantation details/inventory of trees/products to be transported.",
                "Provide a barangay certification for non-timber products, if required.",
                "Pay any required processing fees (typically minimal, subject to change).",
                "Undergo a virtual or on-site verification process scheduled by PENRO Marinduque.",
                "Receive a digital COV within 15–30 working days after approval; present the digital or printed COV during transport."
            ]
        },
        {
            id: "tree-plantation",
            icon: <MapPin className="h-12 w-12 text-green-600" />,
            reactIcon: FaMapMarkerAlt,
            title: "Private Tree Plantation Registration (PTPR)",
            description: "Register your private tree plantations to establish ownership and comply with DENR policies.",
            process: [
                "Complete the online PTPR application form available on the PermitTree website.",
                "Upload proof of land ownership (e.g., land title, tax declaration) and a plantation inventory (species, area, age).",
                "Pay the registration fee (approx. PHP 200–500, subject to change).",
                "Schedule an on-site verification by PENRO Marinduque personnel.",
                "Receive a digital PTPR certificate within 10–20 working days after successful verification."
            ]
        },
        {
            id: "govt-projects",
            icon: <Truck className="h-12 w-12 text-green-600" />,
            reactIcon: FaTruck,
            title: "National Government Agency Projects",
            description: "Get permits for tree cutting or earth balling for trees affected by national government agency projects.",
            process: [
                "Confirm project eligibility with PENRO Marinduque—applies to NGA projects on public or private lands requiring tree removal or transplanting.",
                "Complete the online application form via this website.",
                "Provide a detailed environmental impact assessment (EIA), project plans, and tree inventory (species, location, condition).",
                "Propose a tree replacement or relocation plan (earth balling, if applicable).",
                "Pay the processing fee (varies by project scope, typically PHP 1,000+).",
                "Undergo review by the PENRO Marinduque technical committee and attend a virtual consultation, if required.",
                "Schedule an inspection and assessment by PENRO staff.",
                "Receive a decision within 30–60 working days, pending environmental clearance and approval.",
                "If approved, download the special tree cutting/earth balling permit digitally."
            ]
        },
        {
            id: "private-land-timber",
            icon: <TreePine className="h-12 w-12 text-green-600" />,
            reactIcon: FaTree,
            title: "Private Land Timber Permits",
            description: "Apply for PLTP for non-premium species or SPLTP for premium/naturally-grown trees on private lands.",
            process: [
                "Choose between PLTP (non-premium species) or SPLTP (premium/naturally-grown trees) based on tree species.",
                "Complete the online application form via this website.",
                "Upload proof of land ownership (title/tax declaration) and proof of tree ownership.",
                "Provide a comprehensive tree inventory (species, diameter, volume) and a harvesting plan.",
                "Submit a sustainable management plan for the area to ensure environmental compliance.",
                "Pay the application fee (non-premium: ~PHP 500–1,500; premium: higher, subject to volume and species, subject to change).",
                "Schedule and undergo field validation by PENRO Marinduque staff.",
                "Attend an online seminar on sustainable harvesting practices, if required by PENRO.",
                "Receive a permit decision within 15–30 working days after field validation.",
                "If approved, download the digital permit for immediate use."
            ]
        }
    ];

    // Check if a service was passed from the landing page
    useEffect(() => {
        if (location.state?.selectedService) {
            // Find the matching service in our services array
            const matchingService = services.find(
                service => service.title === location.state.selectedService.title
            );
            if (matchingService) {
                setSelectedService(matchingService);
                // Scroll to the service details section
                setTimeout(() => {
                    document.getElementById('serviceDetails')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 100);
            }
        }
    }, [location.state]);

    const goBack = () => {
        setSelectedService(null);
    };

    const selectService = (service) => {
        setSelectedService(service);
        setTimeout(() => {
            const element = document.getElementById('serviceDetails');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);
    };

    const faqs = [
        {
            question: "How long does it take to process a tree cutting permit?",
            answer: "Processing times vary, but typically it takes 5-10 working days for a standard application. Complex cases may require additional time."
        },
        {
            question: "Do I need to register my chainsaw even if I only use it on my private property?",
            answer: "Yes, all chainsaws must be registered regardless of where they are used. This is in compliance with Republic Act No. 9175 or the Chainsaw Act of 2002."
        },
        {
            question: "What documents do I need to apply for a Private Land Timber Permit?",
            answer: "Required documents typically include proof of land ownership, a sketch map of the area, an inventory of trees to be cut, and a development plan for the land. Specific requirements may vary, so it's best to check with our office for the most up-to-date list."
        },
        {
            question: "How often do I need to renew my chainsaw registration?",
            answer: "Chainsaw registrations are valid for two years and must be renewed before expiration. It's advisable to start the renewal process at least a month before your current registration expires."
        },
        {
            question: "Is there a fee for obtaining a Certificate of Verification (COV)?",
            answer: "Yes, there is a nominal processing fee for COVs. The exact amount can vary, so please contact our office for the current fee structure."
        },
        {
            question: "Can I apply for these permits and registrations online?",
            answer: "Yes! You can now complete the entire application process online through the PermitTree system. This includes document uploads, payments, and receiving your digital permits."
        },
        {
            question: "What happens if I cut a tree without a permit?",
            answer: "Cutting trees without proper permits is illegal and can result in fines and legal consequences. It's always best to obtain the necessary permits before any tree-cutting activity."
        },
        {
            question: "How do I know if the trees on my property are considered 'premium' species?",
            answer: "Premium tree species are defined by DENR regulations and include trees like narra, molave, and kamagong. Our office can provide a full list and help you identify the species on your property."
        }
    ];

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <Header />
            <motion.main
                className="flex-1"
                initial="hidden"
                animate="visible"
                variants={fadeIn}
            >
                <div className="container mx-auto px-4 py-12">
                    <motion.h1
                        className="text-4xl font-bold text-green-800 mb-8 text-center"
                        variants={slideUp}
                    >
                        Our Services
                    </motion.h1>
                    <motion.p
                        className="text-lg text-center mb-12 max-w-3xl mx-auto"
                        variants={slideUp}
                    >
                        DENR PENRO Marinduque offers a comprehensive range of services to help you manage your tree-related activities in compliance with environmental regulations.
                    </motion.p>

                    {/* Services Grid */}
                    <motion.div
                        id="servicesGrid"
                        className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 mb-16"
                        variants={staggerChildren}
                    >
                        {services.map((service, index) => (
                            <Card
                                key={index}
                                className="h-[280px] cursor-pointer transition-shadow hover:shadow-lg"
                                onClick={() => selectService(service)}
                            >
                                <CardHeader className="p-6">
                                    <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center mb-4">
                                        {service.icon}
                                    </div>
                                    <h3 className="text-xl font-semibold text-green-800 whitespace-nowrap">
                                        {service.title}
                                    </h3>
                                </CardHeader>
                                <CardContent className="px-6">
                                    <p className="text-gray-600">
                                        {service.description}
                                    </p>
                                </CardContent>
                                <CardFooter className="px-6 mt-auto border-t border-gray-100">
                                    <button
                                        className="flex items-center text-green-600 font-medium text-sm hover:underline ml-auto"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            selectService(service);
                                        }}
                                    >
                                        View details <ChevronDown className="ml-1 h-4 w-4" />
                                    </button>
                                </CardFooter>
                            </Card>
                        ))}
                    </motion.div>

                    {/* Service Details Section */}
                    {selectedService && (
                        <motion.div
                            id="serviceDetails"
                            className="mb-16 bg-white p-8 rounded-xl shadow-lg border border-gray-100"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            <div className="flex flex-wrap items-center justify-between mb-6">
                                <button
                                    onClick={goBack}
                                    className="flex items-center text-green-600 hover:text-green-700"
                                >
                                    <ArrowLeft className="h-5 w-5 mr-1" /> Back to all services
                                </button>
                                <button
                                    onClick={() => document.getElementById('servicesGrid')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                                    className="flex items-center text-green-600 hover:text-green-700 ml-4"
                                >
                                    <ChevronUp className="h-5 w-5 mr-1" /> Back to Services
                                </button>
                            </div>

                            <div className="flex flex-col md:flex-row items-start mb-8">
                                <div className="bg-green-50 p-6 rounded-xl mb-6 md:mb-0 md:mr-8 flex-shrink-0">
                                    {selectedService.icon}
                                </div>
                                <div>
                                    <h2 className="text-2xl md:text-3xl font-bold text-green-800 mb-3">
                                        {selectedService.title}
                                    </h2>
                                    <p className="text-lg text-gray-600 mb-4">
                                        {selectedService.description}
                                    </p>
                                </div>
                            </div>

                            <div className="bg-gray-50 p-6 rounded-xl">
                                <h3 className="text-xl font-semibold text-green-800 mb-6">Process Overview:</h3>
                                <div className="space-y-4">
                                    {selectedService.process.map((step, idx) => (
                                        <div
                                            key={idx}
                                            className="flex items-start gap-4 bg-white p-4 rounded-lg shadow-sm"
                                        >
                                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-semibold">
                                                {idx + 1}
                                            </div>
                                            <p className="text-gray-700">{step}</p>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-8 flex justify-center">
                                    <Link to="/auth" className="bg-green-600 text-white px-8 py-3 rounded-xl font-medium hover:bg-green-700 transition duration-300 shadow-md">
                                        Apply Now
                                    </Link>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* Call-to-Action Section */}
                    <motion.div
                        className="bg-gradient-to-r from-green-600 to-green-700 text-white p-10 rounded-xl shadow-lg mb-16"
                        variants={fadeIn}
                    >
                        <h2 className="text-3xl font-bold mb-4 text-center">Ready to Apply?</h2>
                        <p className="text-center mb-8 text-lg max-w-2xl mx-auto">
                            Start your application process online and experience a streamlined permitting system designed for efficiency and environmental protection.
                        </p>
                        <div className="flex flex-col sm:flex-row justify-center gap-4">
                            <Link to="/auth" className="bg-white text-green-600 px-8 py-3 rounded-xl font-semibold hover:bg-green-50 transition duration-300 text-center">
                                Apply Now
                            </Link>
                            <Link to="/contact" className="border-2 border-white px-8 py-3 rounded-xl font-semibold hover:bg-green-700 transition duration-300 text-center">
                                Contact Us
                            </Link>
                        </div>
                    </motion.div>

                    {/* Updated FAQ Section using shadcn Accordion */}
                    <motion.div
                        className="mb-16"
                        variants={fadeIn}
                    >
                        <h2 className="text-3xl font-bold text-green-800 mb-8 text-center">Frequently Asked Questions</h2>
                        <Accordion type="single" collapsible className="w-full">
                            {faqs.map((faq, index) => (
                                <AccordionItem key={index} value={`item-${index}`}>
                                    <AccordionTrigger className="text-xl text-green-800 hover:no-underline">
                                        {faq.question}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-gray-600">
                                        {faq.answer}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </motion.div>

                    {/* Contact Information */}
                    {/* <motion.div
                        className="bg-white p-8 rounded-xl shadow-lg mb-16"
                        variants={fadeIn}
                    >
                        <h2 className="text-3xl font-bold text-green-800 mb-6 text-center">Get in Touch</h2>
                        <div className="flex flex-col md:flex-row justify-center gap-6 md:gap-10">
                            <div className="flex items-center">
                                <Phone className="h-6 w-6 text-green-600 mr-3" />
                                <span>(123) 456-7890</span>
                            </div>
                            <div className="flex items-center">
                                <Mail className="h-6 w-6 text-green-600 mr-3" />
                                <span>info@denrpenromarinduque.gov.ph</span>
                            </div>
                        </div>
                    </motion.div> */}
                </div>
            </motion.main>
            <Footer />
        </div>
    );
}
